import { forwardRef, useState, useEffect, Children } from "react";

import { Tabs, TabsProps, useTheme } from "@mui/material";

import { mergeSx } from "@/common/helpers/mergeSx";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";

type FormTabGroupValueType = string | number | false | null;

type FormTabGroupProps = Omit<TabsProps, "variant" | "scrollButtons" | "value" | "onChange"> & {
  value: FormTabGroupValueType;
  scrollable?: boolean;
  onChange?: (event: React.SyntheticEvent, newValue: FormTabGroupValueType) => void;
};

export const FormTabGroup = forwardRef<HTMLDivElement, FormTabGroupProps>(
  ({ children, sx, value, onChange, scrollable, ...rest }, ref) => {
    const theme = useTheme();
    const [internalValue, setInternalValue] = useState<FormTabGroupValueType>(value);

    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    const handleOnChange = (event: React.SyntheticEvent, newValue: FormTabGroupValueType) => {
      setInternalValue(newValue);
      onChange?.(event, newValue);
    };

    const totalTabs = Children.toArray(children).filter((child) => !!child).length;

    if (!theme.palette.forms || !theme.palette.formsComponents?.tabs) {
      return <InvalidThemeErrorMessage />;
    }

    const styles = mergeSx(
      {
        ...(scrollable
          ? {
              ["& .MuiTabs-flexContainer"]: {
                gap: 1,
                // Unfortunately there is a bug in MUI that causes a JS error when variant="scrollable".
                // This is a workaround to prevent the error.
                overflowX: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              },
            }
          : {
              ["& .MuiTabs-flexContainer"]: {
                display: "grid",
                gridTemplateColumns: `repeat(${totalTabs}, 1fr)`,
                gap: 0.5,
                padding: 0.5,
                backgroundColor: theme.palette.formsComponents.tabs.groupBackgroundColor,
                borderRadius: theme.spacing(1.5),
              },
            }),
        ["& .MuiTabs-indicator"]: {
          display: "none",
        },
      },
      sx
    );

    return (
      <Tabs ref={ref} sx={styles} value={internalValue} onChange={handleOnChange} {...rest}>
        {children}
      </Tabs>
    );
  }
);

FormTabGroup.displayName = "FormTabGroup";
